import { ReactNode, useState } from "react";

import {
  Check,
  ChevronDown,
} from "@hl/base-components/lib/assets/icons.generated";
import { TEXT_COLOR } from "@hl/base-components/lib/theme/colors";
import {
  WEIGHT_BOLD,
  WEIGHT_NORMAL,
} from "@hl/base-components/lib/theme/typography";
import { Box, Group, Menu, Stack, Text, UnstyledButton } from "@mantine/core";

import {
  MintVector,
  MintVectors,
  saleTitleMap,
} from "~/features/tokens/sections/ManageToken/MintVector/MultipleMintVector";
import {
  getSaleStatus,
  getStartsInLabel,
  SaleStatus,
} from "~/features/tokens/sections/ManageToken/types";

import { GetManageTokenCollectionQuery } from "../queries.graphql.generated";

export const MintVectorsMenu = ({
  mintVectors,
  onSelected,
  collection,
}: {
  mintVectors: MintVectors;
  collection: GetManageTokenCollectionQuery["getCollection"];
  onSelected: (mintVector: MintVector) => void;
}) => {
  const [selectedMintVector, setSelectedMintVector] =
    useState<MintVector | null>(mintVectors.at(0) || null);

  if (mintVectors.length === 1) {
    return (
      <Box px="md" py="sm">
        <MintVectorTitle
          size="lg"
          bold
          mintVector={selectedMintVector}
          collection={collection}
        />
      </Box>
    );
  }

  return (
    <Menu shadow="md" offset={8} position="bottom-start" width="target">
      <Menu.Target>
        <UnstyledButton w="100%" px="md" py="sm">
          <Group position="apart" align="center" spacing="md">
            <MintVectorTitle
              mintVector={selectedMintVector}
              collection={collection}
              size="lg"
              bold
            />
            <ChevronDown width={20} height={20} />
          </Group>
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown>
        {mintVectors.map((mintVector) => (
          <MenuLink
            key={mintVector.id}
            isActive={selectedMintVector?.id === mintVector.id}
            onClick={() => {
              setSelectedMintVector(mintVector);
              onSelected(mintVector);
            }}
          >
            <MintVectorTitle
              mintVector={mintVector}
              collection={collection}
              size="sm"
            />
          </MenuLink>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
};

const MintVectorTitle = ({
  mintVector,
  collection,
  size,
  bold = false,
}: {
  mintVector: MintVector | null;
  collection: GetManageTokenCollectionQuery["getCollection"];
  size: "sm" | "lg";
  bold?: boolean;
}) => {
  if (!mintVector) return <></>;

  const isDeploying = collection.status === "IN_PROGRESS";

  const saleStatus: SaleStatus = getSaleStatus(mintVector, collection);
  const saleTitle = saleTitleMap[saleStatus];
  return (
    <Stack spacing={0}>
      <Text size={size} fw={bold ? WEIGHT_BOLD : WEIGHT_NORMAL}>
        {mintVector.name}
      </Text>
      <Text
        size="xs"
        color={isDeploying ? TEXT_COLOR.SECONDARY : saleTitle.color}
      >
        {isDeploying
          ? "Deploying"
          : saleTitle.mintStatus.replace(
              ":days",
              getStartsInLabel(new Date(mintVector.start))
            )}
      </Text>
    </Stack>
  );
};

const MenuLink = ({
  isActive,
  onClick,
  children,
}: {
  isActive: boolean;
  onClick: () => void;
  children: ReactNode;
}) => (
  <Menu.Item onClick={onClick} px="md" py="sm">
    <Group position="apart">
      {children}
      {isActive && <Check width={20} height={20} />}
    </Group>
  </Menu.Item>
);
